import { SEO } from "@/components/SEO";
import React, { useState, useEffect } from "react";
import { api, setAdminToken } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Users, Mail, MessageSquare, Sparkles, BookOpen, Plus, Trash2 } from "lucide-react";
import { AREAS, FORMATOS } from "@/data/areas";

export default function Admin() {
  const [logged, setLogged] = useState(!!localStorage.getItem("cometa_admin_token"));
  const [password, setPassword] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [experiencias, setExperiencias] = useState([]);
  const [tejido, setTejido] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [contactos, setContactos] = useState([]);
  const [inscripciones, setInscripciones] = useState([]);
  const [utopias, setUtopias] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [newExp, setNewExp] = useState({ tipo: "Tertulia", area: "Tertulia", formato: "Charla", nombre: "", descripcion: "", slug: "", guia_nombre: "", guia_semblanza: "", fecha: "", hora: "", modalidad: "en linea", precio: "", cupo_maximo: 20, caja_experiencia_disponible: false, imagen: "", activa: true, familia: false });

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/login", { password });
      setAdminToken(data.token); setLogged(true); toast.success("Bienvenida.");
    } catch { toast.error("Contraseña incorrecta."); }
  };

  const loadDashboard = () => api.get("/admin/dashboard").then(({ data }) => setDashboard(data)).catch(() => { setAdminToken(null); setLogged(false); });
  const loadExp = () => api.get("/admin/experiencias").then(({ data }) => setExperiencias(data));
  const loadTejido = () => api.get("/admin/tejido").then(({ data }) => setTejido(data));
  const loadNewsletter = () => api.get("/admin/newsletter").then(({ data }) => setNewsletter(data));
  const loadContactos = () => api.get("/admin/contactos").then(({ data }) => setContactos(data));
  const loadIns = () => api.get("/admin/inscripciones").then(({ data }) => setInscripciones(data));
  const loadUt = () => api.get("/admin/utopias").then(({ data }) => setUtopias(data));

  useEffect(() => {
    if (!logged) return;
    if (tab === "dashboard") loadDashboard();
    if (tab === "experiencias") loadExp();
    if (tab === "tejido") loadTejido();
    if (tab === "newsletter") loadNewsletter();
    if (tab === "contactos") loadContactos();
    if (tab === "inscripciones") loadIns();
    if (tab === "utopias") loadUt();
  }, [logged, tab]);

  const createExp = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newExp, precio: newExp.precio ? parseFloat(newExp.precio) : null, cupo_maximo: parseInt(newExp.cupo_maximo) || 20 };
      await api.post("/admin/experiencias", payload);
      toast.success("Experiencia creada."); setShowForm(false); loadExp();
      setNewExp({ tipo: "Tertulia", area: "Tertulia", formato: "Charla", nombre: "", descripcion: "", slug: "", guia_nombre: "", guia_semblanza: "", fecha: "", hora: "", modalidad: "en linea", precio: "", cupo_maximo: 20, caja_experiencia_disponible: false, imagen: "", activa: true, familia: false });
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const deleteExp = async (slug) => {
    if (!window.confirm("¿Eliminar?")) return;
    await api.delete(`/admin/experiencias/${slug}`); loadExp(); toast.success("Eliminada.");
  };

  const exportCSV = (rows, name) => {
    if (!rows.length) return;
    const cols = Object.keys(rows[0]);
    const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => JSON.stringify(r[c] ?? "")).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${name}.csv`; a.click();
  };

  if (!logged) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-cometa-crema">
      <SEO title="Administración" description="Panel privado." noindex />
        <form onSubmit={login} className="w-full max-w-sm bg-white rounded-3xl p-8 border border-cometa-marino/10">
          <h1 className="text-3xl mb-6">Panel administrativo</h1>
          <input type="password" required placeholder="Contraseña" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full border border-cometa-tinta/15 rounded-xl px-4 py-3 mb-4 focus:outline-none focus:border-cometa-marino" data-testid="admin-password" />
          <button data-testid="admin-login-btn" className="w-full bg-cometa-marino text-cometa-crema py-3 rounded-full font-semibold hover:bg-cometa-tinta transition">Entrar</button>
        </form>
      </div>
    );
  }

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: Sparkles },
    { id: "experiencias", label: "Experiencias", icon: BookOpen },
    { id: "inscripciones", label: "Inscripciones", icon: Users },
    { id: "utopias", label: "Utopías", icon: Sparkles },
    { id: "tejido", label: "Tejido", icon: Sparkles },
    { id: "newsletter", label: "Newsletter", icon: Mail },
    { id: "contactos", label: "Contactos", icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen bg-cometa-crema">
      <div className="border-b border-cometa-tinta/10 bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-display">Admin · Cometa</h1>
          <button onClick={() => { setAdminToken(null); setLogged(false); }} className="text-sm inline-flex items-center gap-1.5 text-cometa-tinta/80"><LogOut size={16} /> Salir</button>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 py-6 flex flex-wrap gap-2 border-b border-cometa-tinta/10">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`} className={`px-4 py-2 rounded-full text-sm font-medium transition ${tab === t.id ? "bg-cometa-marino text-white" : "hover:bg-cometa-tinta/5"}`}>{t.label}</button>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {tab === "dashboard" && dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(dashboard).map(([k, v]) => (
              <div key={k} className="bg-white p-6 rounded-2xl border border-cometa-tinta/10">
                <p className="text-xs tracking-widest text-cometa-tinta/50 uppercase">{k}</p>
                <p className="text-4xl font-display mt-2">{v}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "experiencias" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl">Experiencias</h2>
              <button onClick={() => setShowForm(!showForm)} className="inline-flex items-center gap-2 bg-cometa-marino text-white px-4 py-2 rounded-full text-sm"><Plus size={16} /> Nueva</button>
            </div>
            {showForm && (
              <form onSubmit={createExp} className="bg-white p-6 rounded-2xl border border-cometa-tinta/10 mb-6 grid md:grid-cols-2 gap-3">
                <input required placeholder="Slug (único)" value={newExp.slug} onChange={(e) => setNewExp({ ...newExp, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} className="border rounded-lg px-3 py-2" />
                <select value={newExp.area || "Tertulia"} onChange={(e) => setNewExp({ ...newExp, area: e.target.value, tipo: e.target.value })} className="border rounded-lg px-3 py-2" data-testid="admin-exp-area">
                  {AREAS.map((a) => <option key={a.slug}>{a.nombre}</option>)}
                </select>
                <select value={newExp.formato || "Charla"} onChange={(e) => setNewExp({ ...newExp, formato: e.target.value })} className="border rounded-lg px-3 py-2" data-testid="admin-exp-formato">
                  {FORMATOS.map((f) => <option key={f}>{f}</option>)}
                </select>
                <input required placeholder="Nombre" value={newExp.nombre} onChange={(e) => setNewExp({ ...newExp, nombre: e.target.value })} className="border rounded-lg px-3 py-2 md:col-span-2" />
                <textarea required placeholder="Descripción" value={newExp.descripcion} onChange={(e) => setNewExp({ ...newExp, descripcion: e.target.value })} className="border rounded-lg px-3 py-2 md:col-span-2" rows={3} />
                <input placeholder="Guía (nombre)" value={newExp.guia_nombre} onChange={(e) => setNewExp({ ...newExp, guia_nombre: e.target.value })} className="border rounded-lg px-3 py-2" />
                <input placeholder="Guía (semblanza)" value={newExp.guia_semblanza} onChange={(e) => setNewExp({ ...newExp, guia_semblanza: e.target.value })} className="border rounded-lg px-3 py-2" />
                <input type="date" value={newExp.fecha} onChange={(e) => setNewExp({ ...newExp, fecha: e.target.value })} className="border rounded-lg px-3 py-2" />
                <input placeholder="Hora (19:00)" value={newExp.hora} onChange={(e) => setNewExp({ ...newExp, hora: e.target.value })} className="border rounded-lg px-3 py-2" />
                <select value={newExp.modalidad} onChange={(e) => setNewExp({ ...newExp, modalidad: e.target.value })} className="border rounded-lg px-3 py-2">
                  <option value="en linea">En línea</option><option value="presencial">Presencial</option><option value="hibrida">Híbrida</option>
                </select>
                <input placeholder="Precio (MXN, vacío = próximamente)" value={newExp.precio} onChange={(e) => setNewExp({ ...newExp, precio: e.target.value })} className="border rounded-lg px-3 py-2" />
                <input type="number" placeholder="Cupo máximo" value={newExp.cupo_maximo} onChange={(e) => setNewExp({ ...newExp, cupo_maximo: e.target.value })} className="border rounded-lg px-3 py-2" />
                <input placeholder="URL imagen" value={newExp.imagen} onChange={(e) => setNewExp({ ...newExp, imagen: e.target.value })} className="border rounded-lg px-3 py-2 md:col-span-2" />
                <label className="flex items-center gap-2"><input type="checkbox" checked={newExp.caja_experiencia_disponible} onChange={(e) => setNewExp({ ...newExp, caja_experiencia_disponible: e.target.checked })} /> Caja Experiencia</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={newExp.familia} onChange={(e) => setNewExp({ ...newExp, familia: e.target.checked })} /> Para familia</label>
                <button className="md:col-span-2 bg-cometa-marino text-white py-2 rounded-full">Crear</button>
              </form>
            )}
            <div className="bg-white rounded-2xl border border-cometa-tinta/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-cometa-crema"><tr>{["Tipo", "Nombre", "Fecha", "Cupo", "Precio", "Activa", ""].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr></thead>
                <tbody>
                  {experiencias.map((e) => (
                    <tr key={e.slug} className="border-t"><td className="px-4 py-3">{e.area || e.tipo}{e.formato && ` · ${e.formato}`}</td><td className="px-4 py-3 font-semibold">{e.nombre}</td><td className="px-4 py-3">{e.fecha || "—"}</td><td className="px-4 py-3">{e.inscritos}/{e.cupo_maximo}</td><td className="px-4 py-3">{e.precio ? `$${e.precio}` : "—"}</td><td className="px-4 py-3">{e.activa ? "✓" : "—"}</td><td className="px-4 py-3"><button onClick={() => deleteExp(e.slug)} className="text-cometa-carmesi"><Trash2 size={16} /></button></td></tr>
                  ))}
                  {experiencias.length === 0 && <tr><td colSpan="7" className="px-4 py-8 text-center text-cometa-tinta/50">Sin experiencias.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {["newsletter", "contactos", "inscripciones", "utopias", "tejido"].includes(tab) && (
          <SimpleTable
            title={tab}
            rows={tab === "newsletter" ? newsletter : tab === "contactos" ? contactos : tab === "inscripciones" ? inscripciones : tab === "utopias" ? utopias : tejido}
            onExport={() => exportCSV(tab === "newsletter" ? newsletter : tab === "contactos" ? contactos : tab === "inscripciones" ? inscripciones : tab === "utopias" ? utopias : tejido, tab)}
          />
        )}
      </div>
    </div>
  );
}

function SimpleTable({ title, rows, onExport }) {
  const cols = rows[0] ? Object.keys(rows[0]).slice(0, 6) : [];
  return (
    <div>
      <div className="flex justify-between mb-4"><h2 className="text-2xl capitalize">{title}</h2><button onClick={onExport} className="text-sm bg-cometa-marino text-white px-4 py-2 rounded-full">Exportar CSV</button></div>
      <div className="bg-white rounded-2xl border border-cometa-tinta/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-cometa-crema"><tr>{cols.map((c) => <th key={c} className="text-left px-4 py-3">{c}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => <tr key={i} className="border-t">{cols.map((c) => <td key={c} className="px-4 py-3 truncate max-w-xs">{typeof r[c] === "object" ? JSON.stringify(r[c]) : String(r[c] ?? "—")}</td>)}</tr>)}
            {rows.length === 0 && <tr><td colSpan={cols.length || 1} className="px-4 py-8 text-center text-cometa-tinta/50">Sin datos.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
